import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DataTable, Field } from "@/components/common";
import Modal from "@/components/Modal";
import { Confirm } from "./shared";

const EMPTY = { code: "", name: "", siret: "", vat_number: "", address: "", fiscal_year_end: "12-31", invoice_mailbox: "", first_service: "Général" };
const FIELDS = [["code", "Préfixe *"], ["name", "Raison sociale *"], ["siret", "SIRET"], ["vat_number", "N° TVA"], ["address", "Adresse"], ["fiscal_year_end", "Clôture (MM-JJ)"], ["invoice_mailbox", "Boîte factures"]];

function Form({ initial, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const editing = !!initial.code;
  const save = async () => {
    try {
      editing ? await api.put(`/admin/companies/${initial.code}`, f) : await api.post("/admin/companies", f);
      toast.success("Société enregistrée"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-company-form">
      <div className="grid gap-3 sm:grid-cols-2">{FIELDS.concat(editing ? [] : [["first_service", "Premier service"]]).map(([k, l]) =>
        <Field key={k} label={l}><input className="inp" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`admin-company-${k}-input`} /></Field>)}</div>
      {editing && <p className="text-xs text-[#6B6B80]">{initial.prefix_used ? "Préfixe verrouillé : des documents sont déjà numérotés avec ce préfixe." : "Le préfixe peut encore être modifié : aucun document n'est numéroté avec."}</p>}
      <div className="flex justify-end"><button className="btn-primary" onClick={save} data-testid="admin-company-save-btn">Enregistrer</button></div>
    </div>
  );
}

export default function AdminCompanies() {
  const { loadSettings, refreshUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const load = () => api.get("/admin/companies").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const done = () => { setForm(null); setDel(null); load(); loadSettings(); refreshUser(); };
  const remove = async () => { try { await api.delete(`/admin/companies/${del.code}`); toast.success("Société supprimée"); done(); } catch (e) { toast.error(errMsg(e)); setDel(null); } };
  const columns = [
    { key: "code", label: "Préfixe", render: (r) => <span className="mono font-bold">{r.code}</span> },
    { key: "name", label: "Raison sociale" },
    { key: "services", label: "Services", value: (r) => r.services.join(", ") },
    { key: "documents", label: "Documents", type: "number" },
    { key: "prefix_used", label: "Préfixe utilisé", value: (r) => (r.prefix_used ? "Oui" : "Non") },
    { key: "act", label: "", export: false, sortable: false, render: (r) => <button className="btn-ghost !px-2 text-[#B03A3A]" onClick={(e) => { e.stopPropagation(); setDel(r); }} data-testid={`admin-company-delete-${r.code}`}><Trash2 size={14} /></button> },
  ];
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button className="btn-primary" onClick={() => setForm({})} data-testid="admin-new-company-btn"><Plus size={16} /> Nouvelle société</button></div>
      <DataTable columns={columns} rows={rows} title="Sociétés" testid="admin-companies-table" onRowClick={(r) => setForm(r)} />
      <Modal open={!!form} onClose={() => setForm(null)} title={form?.code ? `Modifier ${form.name}` : "Nouvelle société"} wide testid="admin-company-modal">{form && <Form key={form.code || "new"} initial={form} onSaved={done} />}</Modal>
      <Confirm open={!!del} onClose={() => setDel(null)} title={`Supprimer la société ${del?.name || ""}`} count={del?.documents} message="Les services de la société et les rattachements des utilisateurs seront retirés. Refusé si des documents existent." requireText={del?.code} onConfirm={remove} testid="admin-company-delete-confirm" />
    </div>
  );
}
